import type { NurseResponse, NurseWithRelation } from "@vTypes/nurse-type.ts";
import type { Shift } from "@vTypes/snapshot-type.ts";
import type { AvailableMember } from "@/hooks/useAvailbleMember";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import type { ShiftResponse } from "@/hooks/roster-craete/useShiftsQuery";

dayjs.extend(duration);

const COLOR_ARRAY = [
  "254, 14, 130",
  "14, 89, 198",
  "41, 171, 226",
  "247, 147, 30",
  "141, 198, 63",
  "163, 73, 164",
  "0, 178, 169",
  "255, 121, 85",
];

// 프리셉터 id가 있으면 그걸 기준으로 그룹화
export function groupNursesByPreceptor(list: NurseResponse[]) {
  const preceptorMap = new Map<string, string[]>();

  list.forEach((nurse) => {
    const preceptorID = nurse.preceptor_id;
    const nurseID = nurse.nurse_id;
    if (preceptorID) {
      if (!preceptorMap.has(preceptorID)) {
        preceptorMap.set(preceptorID, []);
      }
      preceptorMap.get(preceptorID)!.push(nurseID);
    }
  });
  return Array.from(preceptorMap.entries()).map(([preceptor, preceptee]) => ({
    preceptor,
    preceptee,
  }));
}

// 프리셉터가 있으면 색상 매핑
export function insertPreceptorData(
  fullNurseSchema: NurseResponse[],
  preceptorGroup: { preceptor: string; preceptee: string[] }[]
) {
  return fullNurseSchema.map((nurse) => {
    const groupIndex = preceptorGroup.findIndex(
      (g) =>
        g.preceptor === nurse.nurse_id || g.preceptee.includes(nurse.nurse_id)
    );

    if (groupIndex === -1) {
      return { ...nurse, preceptor: null };
    }

    const group = preceptorGroup[groupIndex];
    const role = nurse.preceptor_id !== null ? "preceptee" : "preceptor";
    const color = COLOR_ARRAY[groupIndex] ?? "#ccc";

    const or = fullNurseSchema
      .filter((n) => n.nurse_id === group.preceptor)
      .map((n) => n.name);

    const ee = group.preceptee
      .map((eeId) => fullNurseSchema.find((n) => n.nurse_id === eeId)?.name)
      .filter(Boolean);

    return {
      ...nurse,
      preceptor: {
        role,
        group: groupIndex,
        color,
        or,
        ee,
      },
    };
  });
}

// 00
export function secondsToTimeString(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return "";
  return dayjs.duration(seconds, "seconds").format("HH:mm");
}

// 프리셉터는 tee길이 0 이상일때임
export function linkPreceptorRelations(
  nurses: NurseResponse[]
): NurseWithRelation[] {
  const nodes: NurseWithRelation[] = nurses.map((n) => ({
    ...n,
    preceptees: [],
    preceptor: null,
  }));

  const nurseMap = new Map(nodes.map((n) => [n.nurse_id, n]));
  nodes.forEach((node) => {
    if (node.preceptor_id) {
      const preceptorNode = nurseMap.get(node.preceptor_id);
      if (preceptorNode) {
        // 프리셉티 입장에서 자신의 프리셉터 정보를 연결
        node.preceptor = {
          ...preceptorNode,
          preceptees: [], //비워버림
          preceptor: null, // 비워버림
        };
        // 프리셉터 입장에서 자신의 프리셉티를 목록에 추가
        preceptorNode.preceptees.push(node);
      }
    }
  });
  return nodes;
}

export function unlinkPreceptorRelations(
  nurses: NurseWithRelation[]
): NurseResponse[] {
  return nurses.map((nurse) => {
    const { preceptor, preceptees, ...rest } = nurse;
    void preceptor;
    void preceptees;
    return rest;
  });
}

export function processShifts(shiftData: ShiftResponse[] | null | undefined) {
  if (!shiftData || shiftData.length === 0) {
    return [];
  }

  shiftData.sort((a, b) => a.sequence - b.sequence);

  return shiftData.map((shift) => ({
    ...shift,
    start_time: secondsToTimeString(shift.start_time as unknown as number),
    end_time: secondsToTimeString(shift.end_time as unknown as number),
    allday: Boolean(shift.allday),
    auto_schedule: shift.auto_schedule,
  }));
}

export function getDefaultShifts(shifts: Shift[] | undefined | null) {
  if (!shifts || shifts.length === 0) return [];
  const found = [
    shifts.find((item) => item.default_shift === "D"),
    shifts.find((item) => item.default_shift === "E"),
    shifts.find((item) => item.default_shift === "N"),
    shifts.find((item) => item.default_shift === "O"),
  ].filter(Boolean) as Shift[];

  if (found.length > 0) {
    found.sort((a, b) => a.sequence - b.sequence);
    return found;
  }
  return [];
}

export function limitShiftSelection(
  currentSelection: string[],
  newShift: string
): string[] {
  // 이미 선택되어 있다면 제거
  if (currentSelection.includes(newShift)) {
    return currentSelection.filter((s) => s !== newShift);
  }

  // 선택되어 있지 않다면 추가
  const updatedSelection = [...currentSelection, newShift];

  // 2개 초과 시 FIFO (가장 먼저 들어온 것 제거)
  // if (updatedSelection.length > 2) {
  //   return updatedSelection.slice(updatedSelection.length - 2);
  // }

  return updatedSelection;
}

// FIFO로 처리
export function applyShiftFifo(shifts: string[]): string[] {
  // if (shifts.length > 2) {
  //   return shifts.slice(-2);
  // }
  return shifts;
}

export function sortShiftArray(shifts: string[]): string[] {
  return [...shifts].sort();
}

/**
 * AvailableMember를 NurseResponse로 변환
 * 필요한 필드만 매핑: 사번, 이름, 직종, 나이, 경력, 입사일, 연락처
 */
export function convertAvailableMemberToNurseResponse(
  member: AvailableMember,
  groupId: string,
  officeId: string
): NurseResponse {
  // 생년월일로 나이 계산
  const calculateAge = (birthDate: string): number | null => {
    if (!birthDate) return null;
    const birth = dayjs(birthDate);
    if (!birth.isValid()) return null;
    return dayjs().diff(birth, "year");
  };

  // 입사일 변환
  const parseJoiningDate = (dateStr: string) => {
    if (!dateStr) return null;
    const date = dayjs(dateStr);
    return date.isValid() ? date.format("YYYY-MM-DDTHH:mm:ss") : null;
  };

  // 날짜를 문자열로 변환 (birth_date용)
  const parseToString = (dateStr: string): string | null => {
    if (!dateStr) return null;
    const date = dayjs(dateStr);
    return date.isValid() ? date.format("YYYY-MM-DD") : null;
  };

  return {
    account_id: member.account_id,
    active: 0, // 메디통 병원 근무자는 비활성 상태로 시작
    experience: parseInt(member.career, 10) || 0,
    group_id: groupId,
    is_head_nurse:
      member.is_head_nurse === "Y" || member.is_head_nurse === "true",
    is_night_nurse: [],
    joining_date: parseJoiningDate(member.joining_date) as unknown as Date,
    level_: "일반",
    name: member.name,
    nurse_id: member.nurse_id,
    office_id: officeId,
    personal_off_adjustment: 0,
    preceptor_id: null,
    resignation_date: null,
    role: member.duty,
    sequence: 0,
    grade: null,
    weekly_off_weekday: null,
    // emp_num: parseInt(member.emp_num, 10) || 0,
    emp_num: member.emp_num || 0,
    nurse_memo: null,
    age: calculateAge(member.birth_date),
    phone_number: member.phone_number,
    gender: member.gender || null,
    team_id: null,
    is_weekend_off: false,
    fixed_shift: null,
    birth_date: parseToString(member.birth_date),
    // @ts-expect-error - NurseResponse에 없는 필드지만 UI에서 사용하기 위해 추가
    affiliation: member.small_kind_name || member.mb_part_name || "-",
  };
}

/**
 * AvailableMember 배열을 NurseWithRelation 배열로 변환
 */
export function convertAvailableMembersToNurseWithRelation(
  members: AvailableMember[],
  groupId: string,
  officeId: string
): NurseWithRelation[] {
  const nurseResponses = members.map((member) =>
    convertAvailableMemberToNurseResponse(member, groupId, officeId)
  );
  return linkPreceptorRelations(nurseResponses);
}

// function makePreceptorGroupID(list: NurseResponse[]) {
//     const preceptorMap = new Map<string, string[]>();

//     list.forEach((nurse) => {
//       const preceptorID = nurse.preceptor_id;
//       const nurseID = nurse.nurse_id;
//       if (preceptorID) {
//         if (!preceptorMap.has(preceptorID)) {
//           preceptorMap.set(preceptorID, []);
//         }
//         preceptorMap.get(preceptorID)!.push(nurseID);
//       }
//     });
//     const preceptorGroupID = Array.from(preceptorMap.entries()).map(
//       ([preceptor, preceptee]) => ({
//         preceptor,
//         preceptee,
//       })
//     );
//     setPreceptorGroupID(preceptorGroupID);
//   }
